import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, of, tap } from 'rxjs';
import { environment } from '../../../../environment/environment';
import { apiUrls } from '../../../shared/constants/api.constants';
import { setTimesheetActivityAppNames } from '../models/timesheet.models';

export interface ActivityApp {
  id: number;
  name: string;
  category: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AllActivityAppsResponse {
  success: boolean;
  message: string;
  data: {
    apps: ActivityApp[];
  };
}

@Injectable({ providedIn: 'root' })
export class ActivityAppsService {
  private readonly http = inject(HttpClient);

  readonly apps = signal<ActivityApp[]>([]);
  readonly loaded = signal(false);
  readonly loadError = signal(false);

  private readonly nameById = computed(() => {
    const map = new Map<number, string>();
    for (const app of this.apps()) {
      map.set(app.id, app.name.trim());
    }
    return map;
  });

  /** Idempotent — safe to call from multiple components. */
  load(): void {
    if (this.loaded()) return;
    if (environment.enableAllActivityAppsApi !== true) {
      this.loaded.set(true);
      return;
    }
    this.http
      .get<AllActivityAppsResponse>(apiUrls.master.get_all_activity_apps)
      .pipe(
        tap((res) => {
          const list = res?.success && Array.isArray(res.data?.apps) ? res.data.apps : [];
          this.apps.set(list);
          setTimesheetActivityAppNames(list);
          this.loadError.set(false);
        }),
        catchError(() => {
          this.loadError.set(true);
          return of(null);
        }),
      )
      .subscribe(() => this.loaded.set(true));
  }

  nameFor(appId: number | null | undefined, fallback = 'No data found'): string {
    if (appId == null) return fallback;
    return this.nameById().get(Number(appId)) ?? fallback;
  }
}
